import PropTypes from 'prop-types';
import { forwardRef, useEffect, useMemo } from 'react';
import useInvoiceSeries from 'Hooks/useInvoiceSeries';
import Select from 'Components/Atoms/Select/Select';
import { cn } from 'Utils/cn';
import { useTranslation } from 'react-i18next';
import SelectActionButton from 'Components/Atoms/Select/SelectActionButton';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';

const SelectInvoiceSerie = forwardRef(function SelectInvoiceSerie(props, ref) {
    const {
        value,
        disabled,
        onChange,
        className,
        label,
        error,
        highlight,
        onAdd,
        invoiceSubtype,
        refetchOnMount = false,
    } = props;

    const { series } = useInvoiceSeries(refetchOnMount ? { refetchOnMount: true, staleTime: 0, cacheTime: 0 } : {});

    const { t } = useTranslation('invoices');
    const options = useMemo(() => {
        return series
            .filter((serie) => serie.invoice_subtype === invoiceSubtype)
            .map((serie) => ({
                value: serie.id,
                label: (
                    <div className="flex justify-between gap-2 items-center">
                        <span className="truncate">{serie.name}</span>
                        <span className="text-gray-500 text-2xs">{serie.next_identifier}</span>
                    </div>
                ),
            }));
    }, [series, invoiceSubtype]);

    useEffect(() => {
        if (value && !options.find((option) => option.value === value)) {
            if (options.length > 0) {
                onChange(options[0].value);
            } else {
                onChange(null);
            }
        }

        if (!value && options.length > 0) {
            onChange(options[0].value);
        }
    }, [options]);

    const actions = useMemo(() => {
        if (!onAdd) return [];
        return [
            <SelectActionButton icon={<OutlineAdd size={18} />} key="action-add-invoice-serie" onClick={() => onAdd()}>
                {t('add-new-serie')}
            </SelectActionButton>,
        ];
    }, [onAdd, t]);

    return (
        <Select
            enableClear={!!value}
            onClear={() => onChange(null)}
            ref={ref}
            disabled={disabled}
            label={label}
            className={cn('w-full', className)}
            options={options}
            onChange={onChange}
            value={value}
            error={error}
            highlight={highlight}
            actions={actions}
        />
    );
});

export default SelectInvoiceSerie;

SelectInvoiceSerie.propTypes = {
    disabled: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    className: PropTypes.string,
    highlight: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onAdd: PropTypes.func,
    invoiceSubtype: PropTypes.string,
    refetchOnMount: PropTypes.bool,
};
